import React, { useState } from "react";
import {
	Box,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import { chronos } from "nhb-toolbox";
import dayjs from "dayjs";
import { useApiMutation } from "src/hooks/useApiMutation";
import type { IB2BSession } from "src/Types/module/b2bSession/b2bSession.types";
import DeleteConfirmation from "src/Utils/DeleteConfirmation";
import UpdateB2BSessionModal from "./UpdateB2BSessionModal";

interface Props {
	session: IB2BSession;
	serial?: number;
}

const formatDisplayTime = (timeStr?: string) => {
	if (!timeStr) return "";
	const [hours, minutes] = timeStr.split(":");
	if (hours === undefined || minutes === undefined) return timeStr;
	const dummyDate = dayjs().hour(Number(hours)).minute(Number(minutes));
	return dummyDate.format("hh:mm A");
};

const B2BSessionTableRow: React.FC<Props> = ({ session, serial }) => {
	const theme = useTheme();
	const [openEdit, setOpenEdit] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);

	const { mutate: deleteSession, isPending: isDeleting } = useApiMutation<
		void,
		void
	>({
		method: "DELETE",
		endPoint: `/api/fair/admin/sessions/${session.id}`,
		invalidateQueries: ["B2BSession", "B2BSessions"],
	});

	const formattedDate = session.date
		? dayjs(session.date).format("YYYY-MM-DD")
		: "—";

	const startTimeFormatted = formatDisplayTime(session.start_time);
	const endTimeFormatted = formatDisplayTime(session.end_time);

	const handleDelete = () => {
		deleteSession(undefined, {
			onSuccess: () => {
				setOpenDeleteModal(false);
			},
		});
	};

	return (
		<>
			<TableRow hover>
				{/* SL. */}
				<TableCell>{serial ?? session.id}</TableCell>

				{/* Session Name */}
				<TableCell>{session.session_no || `Session ${session.id}`}</TableCell>

				{/* Date */}
				<TableCell>{formattedDate}</TableCell>

				{/* Time Slot */}
				<TableCell>
					{startTimeFormatted} – {endTimeFormatted}
				</TableCell>

				{/* Fair ID */}
				<TableCell>{session.fair_id}</TableCell>

				{/* Created At */}
				<TableCell>
					{session.created_at
						? chronos(session.created_at).formatStrict(
								"dd, mmm Do, yy; hh:mm A"
						  )
						: "—"}
				</TableCell>

				{/* Actions */}
				<TableCell align="right">
					<Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
						<Tooltip title="Edit Session" arrow>
							<IconButton
								onClick={() => setOpenEdit(true)}
								size="small"
								sx={{
									color: theme.palette.success.dark,
									"&:hover": {
										bgcolor: theme.colors.success.lighter,
									},
								}}
							>
								<EditTwoToneIcon fontSize="small" />
							</IconButton>
						</Tooltip>

						<DeleteConfirmation
							handleClose={() => setOpenDeleteModal(false)}
							handleDeleteFun={handleDelete}
							text={`Are you sure you want to delete "${session.session_no}" scheduled on ${session.date}?`}
							loading={isDeleting}
						/>
					</Box>
				</TableCell>
			</TableRow>

			{/* Edit Modal */}
			{openEdit && (
				<UpdateB2BSessionModal
					open={openEdit}
					handleClose={() => setOpenEdit(false)}
					session={session}
				/>
			)}
		</>
	);
};

export default B2BSessionTableRow;
